import React from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import Layout from './Layout';
import Dashboard from '../pages/Dashboard';
import Login from '../pages/Login';
import RequireAuth from './RequireAuth';
import NewCustomer from '../pages/NewCustomer';
import Accounts from '../pages/Accounts';
import AccountDetails from '../pages/AccountDetails';
import BillingLayout from '../pages/billing/BillingLayout';
import CustomerLookup from '../pages/billing/CustomerLookup';
import CurrentBill from '../pages/billing/CurrentBill';
import CollectPayment from '../pages/billing/CollectPayment';
import BillingHistory from '../pages/billing/BillingHistory';
import PaymentPlans from '../pages/billing/PaymentPlans';
import ServiceRequestsLayout from '../pages/service-requests/ServiceRequestsLayout';
import SubmitRequest from '../pages/service-requests/SubmitRequest';
import MyRequests from '../pages/service-requests/MyRequests';
import PlaceholderPage from './PlaceholderPage';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../context/useAuth';

const LandingRoute = () => {
  const { isAuthenticated, isInitializing } = useAuth();

  if (isInitializing) {
    return (
      <div className="container py-5 app-session-loading" aria-busy="true" aria-live="polite">
        <div className="d-flex align-items-center gap-2 text-muted app-session-loading__content">
          <div className="spinner-border spinner-border-sm" role="status" aria-label="Loading"></div>
          <span>Checking session…</span>
        </div>
      </div>
    );
  }

  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
};

/**
 * AppRoutes component that handles routing and automatic page title management
 */
const AppRoutes = () => {
  // Automatically manages page titles based on current route
  usePageTitle();

  const { isAuthenticated, isInitializing } = useAuth();

  if (isInitializing) {
    return (
      <div className="container py-5 app-session-loading" aria-busy="true" aria-live="polite">
        <div className="d-flex align-items-center gap-2 text-muted app-session-loading__content">
          <div className="spinner-border spinner-border-sm" role="status" aria-label="Loading"></div>
          <span>Checking session…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LandingRoute />} />
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="accounts/:accountId" element={<AccountDetails />} />
        <Route path="new-customer" element={<NewCustomer />} />
        <Route path="billing" element={<BillingLayout />}>
          <Route index element={<CustomerLookup />} />
          <Route path="customer-lookup" element={<CustomerLookup />} />
          <Route path="current-bill" element={<CurrentBill />} />
          <Route path="collect-payment" element={<CollectPayment />} />
          <Route path="billing-history" element={<BillingHistory />} />
          <Route path="payment-plans" element={<PaymentPlans />} />
        </Route>
        <Route 
          path="move-in" 
          element={
            <PlaceholderPage 
              title="Move-In Service"
              icon="box-arrow-in-right"
              description="Process utility connection for new address"
              iconColor="text-success"
            />
          } 
        />
        <Route 
          path="move-out" 
          element={
            <PlaceholderPage 
              title="Move-Out Service"
              icon="box-arrow-left"
              description="Process utility disconnection for address"
              iconColor="text-warning"
            />
          } 
        />
        <Route 
          path="change-address" 
          element={
            <PlaceholderPage 
              title="Change of Address"
              icon="house-gear"
              description="Update customer service address"
              iconColor="text-info"
            />
          } 
        />
        <Route path="service-requests" element={<ServiceRequestsLayout />}>
          <Route index element={<SubmitRequest />} />
          <Route path="submit" element={<SubmitRequest />} />
          <Route path="my-requests" element={<MyRequests />} />
        </Route>
        <Route 
          path="work-orders" 
          element={
            <PlaceholderPage 
              title="Work Orders"
              icon="clipboard-check"
              description="Manage field service and maintenance tasks"
              iconColor="text-primary"
            />
          } 
        />
        <Route 
          path="outage-map" 
          element={
            <PlaceholderPage 
              title="Outage Map"
              icon="exclamation-triangle"
              description="View current power outages and restoration status"
              iconColor="text-danger"
            />
          } 
        />
        <Route 
          path="customer-lookup" 
          element={
            <PlaceholderPage 
              title="Customer Lookup"
              icon="search"
              description="Search and view customer account information"
              iconColor="text-primary"
            />
          } 
        />
        <Route 
          path="reports" 
          element={
            <PlaceholderPage 
              title="Reports & Analytics"
              icon="graph-up"
              description="View system reports and customer analytics"
              iconColor="text-success"
            />
          } 
        />
        <Route 
          path="settings" 
          element={
            <PlaceholderPage 
              title="System Settings"
              icon="gear"
              description="Configure application settings and preferences"
              iconColor="text-secondary"
            />
          } 
        />
        <Route 
          path="profile" 
          element={
            <PlaceholderPage 
              title="User Profile"
              icon="person-circle"
              description="Manage your user profile and account settings"
              iconColor="text-info"
            />
          } 
        />
        <Route 
          path="help" 
          element={
            <PlaceholderPage 
              title="Help & Support"
              icon="question-circle"
              description="Access help documentation and support resources"
              iconColor="text-primary"
            />
          } 
        />
      </Route>
      <Route path="*" element={<LandingRoute />} />
    </Routes>
  );
};

export default AppRoutes;
